import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  AgentSession, AgentKind, AgentStatus, Effort, Message, Attachment, ToolCall, PanelState
} from '../agents/types';
import { AGENT_REGISTRY } from '../agents/registry';
import { effortLevelsFor } from '../providers/registry';
import { shortId } from '../utils';
import { MOCK_SESSIONS } from '../mock/sessions';

interface SessionStore {
  sessions: AgentSession[];
  activeId: string | null;

  active(): AgentSession | undefined;
  setActive(id: string): void;
  newSession(kind: AgentKind, name?: string): AgentSession;
  removeSession(id: string): void;
  renameSession(id: string, name: string): void;
  archiveSession(id: string): void;
  restoreSession(id: string): void;
  setAgentKind(sessionId: string, kind: AgentKind): void;

  setModel(sessionId: string, modelId: string): void;
  setEffort(sessionId: string, effort: Effort): void;
  setStatus(sessionId: string, status: AgentStatus): void;
  appendMessage(sessionId: string, message: Message): void;
  clearMessages(sessionId: string): void;

  addAttachments(sessionId: string, items: Attachment[]): void;
  removeAttachment(sessionId: string, attachmentId: string): void;

  // Panel state — driven by Claude via MCP, mirrored from backend SSE events.
  panelStates: Record<string, PanelState>;
  setPanelState(sessionId: string, patch: Partial<PanelState>): void;

  // streaming helpers — mutate parts on a specific message
  appendTextDelta(sessionId: string, messageId: string, delta: string): void;
  appendReasoningDelta(sessionId: string, messageId: string, delta: string): void;
  appendToolCallPart(sessionId: string, messageId: string, toolCall: ToolCall): void;
  updateToolCall(
    sessionId: string,
    messageId: string,
    toolCallId: string,
    patch: Partial<ToolCall>
  ): void;
  setTrailingStatus(sessionId: string, messageId: string, status: string): void;
}

// Helper: produce a new message with one of its parts mutated.
function mutateMessage(
  messages: Message[],
  messageId: string,
  fn: (m: Message) => Message
): Message[] {
  return messages.map((m) => (m.id === messageId ? fn(m) : m));
}

function lastIndex<T>(arr: T[], pred: (x: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) if (pred(arr[i])) return i;
  return -1;
}

export const useSessionStore = create<SessionStore>()(persist((set, get) => ({
  sessions: MOCK_SESSIONS,
  activeId: MOCK_SESSIONS[0]?.id ?? null,
  panelStates: {},

  active: () => get().sessions.find((s) => s.id === get().activeId),

  setActive: (id) => set({ activeId: id }),

  newSession: (kind, name) => {
    const def = AGENT_REGISTRY[kind];
    const session: AgentSession = {
      id: shortId('s'),
      name: name ?? def.label,
      agentKind: kind,
      providerId: def.defaultProvider,
      modelId: def.defaultModel,
      effort: 'medium',
      status: 'idle',
      messages: [],
      availableTools: def.defaultTools,
      attachments: [],
      metadata: {},
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    set((s) => ({ sessions: [...s.sessions, session], activeId: session.id }));
    return session;
  },

  removeSession: (id) =>
    set((s) => {
      const sessions = s.sessions.filter((x) => x.id !== id);
      const activeId =
        s.activeId === id ? sessions[0]?.id ?? null : s.activeId;
      return { sessions, activeId };
    }),

  renameSession: (id, name) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === id ? { ...x, name: name.trim() || x.name, updatedAt: Date.now() } : x
      ),
    })),

  archiveSession: (id) =>
    set((s) => {
      const sessions = s.sessions.map((x) =>
        x.id === id ? { ...x, archived: true, updatedAt: Date.now() } : x
      );
      const activeId =
        s.activeId === id ? sessions.find((x) => !x.archived)?.id ?? null : s.activeId;
      return { sessions, activeId };
    }),

  restoreSession: (id) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === id ? { ...x, archived: false, updatedAt: Date.now() } : x
      ),
    })),

  setAgentKind: (sessionId, kind) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        const def = AGENT_REGISTRY[kind];
        return {
          ...x,
          agentKind: kind,
          providerId: def.defaultProvider,
          modelId: def.defaultModel,
          availableTools: def.defaultTools,
          updatedAt: Date.now(),
        };
      }),
    })),

  setModel: (sessionId, modelId) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        const allowed = effortLevelsFor(modelId);
        // Clamp effort if the new provider doesn't support the current
        // value; if the provider has no levels at all, the picker hides
        // itself anyway so the stored value is harmless.
        const effort = allowed.length === 0 || allowed.includes(x.effort)
          ? x.effort
          : allowed[allowed.length - 1];
        return { ...x, modelId, effort, updatedAt: Date.now() };
      }),
    })),

  setEffort: (sessionId, effort) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === sessionId ? { ...x, effort, updatedAt: Date.now() } : x
      ),
    })),

  setStatus: (sessionId, status) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === sessionId ? { ...x, status, updatedAt: Date.now() } : x
      ),
    })),

  appendMessage: (sessionId, message) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === sessionId
          ? { ...x, messages: [...x.messages, message], updatedAt: Date.now() }
          : x
      ),
    })),

  clearMessages: (sessionId) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === sessionId ? { ...x, messages: [], updatedAt: Date.now() } : x
      ),
    })),

  addAttachments: (sessionId, items) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === sessionId
          ? { ...x, attachments: [...x.attachments, ...items], updatedAt: Date.now() }
          : x
      ),
    })),

  removeAttachment: (sessionId, attachmentId) =>
    set((s) => ({
      sessions: s.sessions.map((x) =>
        x.id === sessionId
          ? { ...x, attachments: x.attachments.filter((a) => a.id !== attachmentId), updatedAt: Date.now() }
          : x
      ),
    })),

  setPanelState: (sessionId, patch) =>
    set((s) => ({
      panelStates: {
        ...s.panelStates,
        [sessionId]: { ...(s.panelStates[sessionId] ?? {}), ...patch },
      },
    })),

  appendTextDelta: (sessionId, messageId, delta) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        return {
          ...x,
          messages: mutateMessage(x.messages, messageId, (m) => {
            const lastIdx = lastIndex(m.parts, (p) => p.type === 'text');
            if (lastIdx === -1) {
              return { ...m, parts: [...m.parts, { type: 'text', text: delta }] };
            }
            const next = [...m.parts];
            const cur = next[lastIdx] as { type: 'text'; text: string };
            next[lastIdx] = { type: 'text', text: cur.text + delta };
            return { ...m, parts: next };
          }),
          updatedAt: Date.now(),
        };
      }),
    })),

  appendReasoningDelta: (sessionId, messageId, delta) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        return {
          ...x,
          messages: mutateMessage(x.messages, messageId, (m) => {
            const lastIdx = lastIndex(m.parts, (p) => p.type === 'reasoning');
            // attach reasoning only at the start; once a non-reasoning part lands, start a new block
            if (lastIdx === -1 || lastIdx !== m.parts.length - 1) {
              return { ...m, parts: [...m.parts, { type: 'reasoning', text: delta }] };
            }
            const next = [...m.parts];
            const cur = next[lastIdx] as { type: 'reasoning'; text: string };
            next[lastIdx] = { type: 'reasoning', text: cur.text + delta };
            return { ...m, parts: next };
          }),
          updatedAt: Date.now(),
        };
      }),
    })),

  appendToolCallPart: (sessionId, messageId, toolCall) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        return {
          ...x,
          messages: mutateMessage(x.messages, messageId, (m) => ({
            ...m,
            parts: [...m.parts, { type: 'tool-call', toolCall }],
          })),
          updatedAt: Date.now(),
        };
      }),
    })),

  updateToolCall: (sessionId, messageId, toolCallId, patch) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        return {
          ...x,
          messages: mutateMessage(x.messages, messageId, (m) => ({
            ...m,
            parts: m.parts.map((p) =>
              p.type === 'tool-call' && p.toolCall.id === toolCallId
                ? { type: 'tool-call' as const, toolCall: { ...p.toolCall, ...patch } }
                : p
            ),
          })),
          updatedAt: Date.now(),
        };
      }),
    })),

  setTrailingStatus: (sessionId, messageId, status) =>
    set((s) => ({
      sessions: s.sessions.map((x) => {
        if (x.id !== sessionId) return x;
        return {
          ...x,
          messages: mutateMessage(x.messages, messageId, (m) => ({
            ...m,
            trailingStatus: status,
          })),
          updatedAt: Date.now(),
        };
      }),
    })),
}), {
  name: 'xoro.session.v1',
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({
    sessions: state.sessions.map((s) => ({ ...s, status: 'idle' as const })),
    activeId: state.activeId,
    panelStates: state.panelStates,
  }),
}));
